import classnames from "classnames";
import React from "react";
import { ResponsiveBreakpoints } from "../../../../../packages/ui/src/utils/uiHelpers";
import { useWindowDimensions } from "../../atoms";
import ProfileExpand from "../../molecules/profileExpand/profileExpand";

interface Props {
  profiles: IUserPublic[];
}

const ProfileGrid = ({ profiles }: Props) => {
  const { width } = useWindowDimensions();
  const isLargeMobile: boolean = (width > 580 && width < ResponsiveBreakpoints.tablet);

  enum Device {
    desktop = "desktop",
    tablet = "tablet",
    largeMobile = "largeMobile",
  }
  let device: Device;

  if (width > ResponsiveBreakpoints.desktop) {
    device = Device.desktop;
  } else if (width >= ResponsiveBreakpoints.tablet) {
    device = Device.tablet;
  } else {
    device = Device.largeMobile;
  }

  const [expandedId, setExpandedId] = React.useState(null);
  const [rows, setRows] = React.useState<{
    [id: string]: {
      [Device.largeMobile]: number,
      [Device.tablet]: number,
      [Device.desktop]: number,
    },
  }>({});

  // the value of "rows" is used to set the order of each profiles "description/html" block.
  // For each profile we want the block description to take up a full column below the profiles row.
  React.useEffect(() => {
    // store desktop and tablet row number against each user id
    const rowNumber = {};
    let currentLargeMobileRow = 2;
    let currentTabletRow = 3;
    let currentDesktopRow = 4;

    for (let x = 0; x < profiles.length; x++) {
      // For every multiple of 2 set currentLargeMobileRow
      if ((x % 2) === 0 && x > 0) {
        currentLargeMobileRow = currentLargeMobileRow + 2;
      }
      // For every multiple of 3 set currentTabletRow
      if ((x % 3) === 0 && x > 0) {
        currentTabletRow = currentTabletRow + 3;
      }
      // For every multiple of 4 set currentDesktopRow
      if ((x % 4) === 0 && x > 0) {
        currentDesktopRow = currentDesktopRow + 4;
      }

      // for each profile set the relevant row number for each device
      rowNumber[profiles[x].id] = {
        [Device.largeMobile]: currentLargeMobileRow,
        [Device.tablet]: currentTabletRow,
        [Device.desktop]: currentDesktopRow,
      };
    }
    setRows(rowNumber);
  }, []);

  const handleExpansion = React.useCallback((profileId: number) => {
    setExpandedId(expandedId === profileId ? null : profileId);
  }, [expandedId]);

  // if there are no rows don't render
  if ((Object.keys(rows).length === 0 && rows.constructor === Object)) {
    return null;
  }

  const columnsClass = classnames("columns is-multiline", { "is-mobile": isLargeMobile });
  const profileClass = classnames("is-4-tablet is-3-desktop", { "is-6-mobile": isLargeMobile });

  return (
    <div className="profile-grid">
      <div className={columnsClass}>
        {profiles.map((profile: IUserPublic, index) => (<ProfileExpand
          user={profile}
          className={profileClass}
          inGrid={{
            handleExpansion: () => handleExpansion(profile.id),
            isExpanded: expandedId === profile.id,
            order: index + 1,
            row: rows[profile.id][device],
          }}
          key={profile.id}
        />))}
      </div>
    </div>
  );
};

export default ProfileGrid;
